/**
 * @file SwitchableChat.js
 * @author Simon Tenedero, Jonas Matulis
 * @created 2024-XX-XX
 * @lastModified 2025-06-04
 * @description file containing SwitchableChat component
 */

import React, { useState, useEffect } from "react";
import NativeChat from "../NativeChat/NativeChat";
import PrivateChat from "../PrivateChat/PrivateChat";
import PrivateChatSection from "../PrivateChat/PrivateChatSection";
import useActiveUsers from "../../hooks/useActiveUsers";
import LiveChatContainer from "../YoutubeChat/LiveChatContainer";
import ChatTabs from "./ChatTabs";
import {
  ArrowLeftStartOnRectangleIcon,
  ArrowRightStartOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { AVAILABLE_FEATURES } from "../../utils/roleUtils";
import FeatureGate from "../Gates/FeatureGate";
import { extractVideoIdFromUrl } from "../../utils/livestreamsUtils";

/**
 * SwitchableChat component for handling and displaying different chat types (Youtube, Native, PrivateTabs, PrivateChat)
 *
 * @param {*} props - The component properties
 * @param {string} props.videoUrl - Current YouTube video URL.
 * @param {Function} props.setVideoUrl - Function to set video URL.
 * @param {Array} props.privateChats - Array of all private chats.
 * @param {string} props.selectedPrivateChat - Current selected private chat.
 * @param {Function} props.setSelectedPrivateChat - Function to set the selected private chat
 * @param {Map} props.privateChatMembers - Map where key-val pairs are privateChatId and its members
 * @param {Function} props.setPrivateChatMembers - Function to set the private chat members
 * @param {Array} props.selectedPrivateChatMembers - The currently selected private chat members.
 * @param {Function} props.setSelectedPrivateChatMembers - Function to set the selected private chat members.
 * @param {Array} props.pendingPrivateChatMembers - The currently pending private chat members.
 * @param {Array} props.selectedPrivateChatPendingMembers - The currently selected private chat pending members.
 * @param {Function} props.setSelectedPrivateChatPendingMembers - Function to set the selected private chat pending members.
 * @param {Array} props.invitations - List of invitation objects.
 * @param {Boolean} props.chatOpen - State variable tracking if chat is open or not.
 * @param {Function} props.setChatOpen - Function to set the chat open state.
 * @param {Boolean} props.loadingChatSettings - State variable tracking if chat settings are loading.
 *
 * @returns {JSX.Element} The rendered switchable chat component
 */
const SwitchableChat = ({
  videoUrl,
  setVideoUrl,
  privateChats,
  selectedPrivateChat,
  setSelectedPrivateChat,
  privateChatMembers,
  selectedPrivateChatMembers,
  setSelectedPrivateChatMembers,
  pendingPrivateChatMembers,
  selectedPrivateChatPendingMembers,
  setSelectedPrivateChatPendingMembers,
  invitations,
  chatOpen,
  setChatOpen,
  loadingChatSettings,
}) => {
  const [selectedTab, setSelectedTab] = useState("youtubeTab"); // Default tab (youtubeTab, nativeTab, privateTab)
  const [showLoginAlert, setShowLoginAlert] = useState(false);
  const [selectedPrivateTab, setSelectedPrivateTab] = useState("messagesTab"); // for privateTab panel (messageTab, invitationsTab)

  const { activeUsers, fetchActiveUsers } = useActiveUsers(
    extractVideoIdFromUrl(videoUrl),
  );

  /*
  const embedDomain =
    window.location.hostname === "localhost"
      ? "localhost"
      : "csl-kpop-platform.socs.uoguelph.ca";
  const chatSrc = `https://www.youtube.com/live_chat?v=${extractVideoIdFromUrl(videoUrl)}&embed_domain=${embedDomain}`;
  */

  const chatSrc = `https://www.youtube.com/live_chat?v=${extractVideoIdFromUrl(videoUrl)}&embed_domain=${window.location.hostname}`;

  /**
   * Updates the video URL using the given tab ID.
   *
   * @param {string} tabId - The ID of the selected tab.
   */
  const updateVideoUrl = async (videoUrl) => {
    try {
      if (videoUrl) {
        setVideoUrl(videoUrl);
        console.log("Video URL updated to:", videoUrl);
      } else {
        console.error("video URL is not available.");
      }
    } catch (error) {
      console.error("Error updating video URL:", error);
    }
  };

  /*uncomment native tab to display*/
  const mainTabs = [
    {
      id: "youtubeTab",
      name: "youtube chat",
      flag: AVAILABLE_FEATURES.YOUTUBE_CHAT,
    },
    {
      id: "nativeTab",
      name: "native chat",
      flag: AVAILABLE_FEATURES.NATIVE_CHAT,
    },
    {
      id: "privateTab",
      name: "private chat",
      flag: AVAILABLE_FEATURES.PRIVATE_CHAT,
    },
  ];

  const privateTabs = [
    { id: "messagesTab", name: "private chats" },
    { id: "invitationsTab", name: "invitations" },
  ];

  return (
    <div className="flex">
      <div className={"md:mr-2"}>
        <button
          button
          className={`portrait:hidden`}
          onClick={() => setChatOpen(!chatOpen)}
        >
          {chatOpen ? (
            <ArrowRightStartOnRectangleIcon className="h-7 w-7 text-white" />
          ) : (
            <ArrowLeftStartOnRectangleIcon className="h-7 w-7 text-white" />
          )}
        </button>
      </div>

      <div
        className={`
        ${!chatOpen && "landscape:hidden"} 
        desktop:w-[25vw] desktop:h-[85vh]
        xl:w-chat-iPadPro-landscape xl:h-chat-iPadPro-landscape 
        lg:w-chat-tablet-landscape lg:h-chat-tablet-landscape 
        ipadpro-portrait:min-w-[50vw] ipadpro-portrait:max-h-[60vh]
        ipadmini-portrait:w-chat-tablet-portrait ipadmini-portrait:h-chat-tablet-portrait
        iphone-landscape:min-h-[575px] iphone-landscape:max-w-[350px]
        sm:w-chat-mobile-landscape sm:h-chat-mobile-landscape 
        w-chat-mobile-portrait h-chat-mobile-portrait`}
      >
        <ChatTabs
          chatOpen={chatOpen}
          tabs={mainTabs}
          selectedTab={selectedTab}
          onSelectTab={setSelectedTab}
        />
        <div className="chat-content flex-grow h-[90%]">
          {selectedTab === "youtubeTab" ? (
            <FeatureGate flag={AVAILABLE_FEATURES.YOUTUBE_CHAT}>
              <LiveChatContainer chatSrc={chatSrc} />
            </FeatureGate>
          ) : selectedTab === "nativeTab" ? (
            <FeatureGate flag={AVAILABLE_FEATURES.NATIVE_CHAT}>
              <div className="w-full h-full rounded-md">
                <NativeChat
                  videoId={extractVideoIdFromUrl(videoUrl)}
                  activeUsers={activeUsers}
                />
              </div>
            </FeatureGate>
          ) : selectedTab === "privateTab" ? (
            <FeatureGate flag={AVAILABLE_FEATURES.PRIVATE_CHAT}>
              <div className="w-full h-full bg-neutral rounded-md">
                {/*under private tab we could see all the chats or be in one specific chat*/}
                {selectedPrivateChat === null ? (
                  <>
                    <PrivateChatSection
                      privateChats={privateChats}
                      setSelectedPrivateChat={setSelectedPrivateChat}
                      privateChatMembers={privateChatMembers}
                      setSelectedPrivateChatMembers={
                        setSelectedPrivateChatMembers
                      }
                      pendingPrivateChatMembers={pendingPrivateChatMembers}
                      setSelectedPrivateChatPendingMembers={
                        setSelectedPrivateChatPendingMembers
                      }
                      privateTabs={privateTabs}
                      selectedPrivateTab={selectedPrivateTab}
                      setSelectedPrivateTab={setSelectedPrivateTab}
                      invitations={invitations}
                      setNotification={() => {}}
                    />
                  </>
                ) : (
                  <PrivateChat
                    selectedPrivateChat={selectedPrivateChat}
                    videoUrl={videoUrl}
                    updateVideoUrl={updateVideoUrl}
                    selectedPrivateChatMembers={
                      selectedPrivateChatMembers || []
                    }
                    setSelectedPrivateChatMembers={
                      setSelectedPrivateChatMembers
                    }
                    selectedPrivateChatPendingMembers={
                      selectedPrivateChatPendingMembers
                    }
                    setSelectedPrivateChatPendingMembers={
                      setSelectedPrivateChatPendingMembers
                    }
                    setSelectedTab={setSelectedTab}
                    setSelectedPrivateChat={setSelectedPrivateChat}
                    loadingChatSettings={loadingChatSettings}
                  />
                )}
              </div>
            </FeatureGate>
          ) : (
            <>invalid page</>
          )}
        </div>

        {showLoginAlert && (
          <div className="active-users-modal fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
            <div className="modal-content bg-primary rounded-lg shadow-lg p-4 w-full max-w-lg dark:bg-gray-800">
              <span
                className="close text-red-500 hover:text-red-800 cursor-pointer float-right"
                onClick={() => {
                  setShowLoginAlert(false);
                }}
              >
                &times;
              </span>
              <h2 className="text-current text-2xl font-semibold m-4 text-center">
                You must Login to chat in the Native or Private Chats!
              </h2>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SwitchableChat;
